import { supabase } from '../../lib/supabase'
import { invokeEdgeFunction } from '../../lib/supabaseEdge'

/** Base44-style sort: "-created_date" → descending created_at */
function parseSort(sort) {
  if (!sort) return { column: 'created_at', ascending: false }
  const ascending = !sort.startsWith('-')
  const raw = ascending ? sort : sort.slice(1)
  const columnMap = {
    created_date: 'created_at',
    sweep_timestamp: 'sweep_timestamp',
  }
  const column = columnMap[raw] || raw
  return { column, ascending }
}

async function listTable(table, sort, limit) {
  const { column, ascending } = parseSort(sort || '-created_at')
  let q = supabase.from(table).select('*').order(column, { ascending })
  if (limit) q = q.limit(limit)
  const { data, error } = await q
  throwIfError(error)
  return data || []
}

function stamp(row) {
  return { ...row, updated_at: new Date().toISOString() }
}

/** Postgres date columns reject ''; CHECK enums need NULL not ''. */
function cleanRaceRow(row) {
  const out = { ...row }
  ;['election_date', 'runoff_date', 'last_digital_sweep', 'last_linear_sweep'].forEach((k) => {
    if (out[k] === '' || out[k] === undefined) delete out[k]
  })
  ;['election_type', 'ge_rating', 'district', 'team', 'markets', 'reference_link', 'notes'].forEach((k) => {
    if (out[k] === '' || out[k] === undefined) delete out[k]
  })
  if (out.notes_updated_at === '') delete out.notes_updated_at
  if (out.serper_query_fields !== undefined && out.serper_query_fields !== null) {
    if (!Array.isArray(out.serper_query_fields)) delete out.serper_query_fields
  }
  if (out.serper_mode !== undefined && out.serper_mode !== null) {
    const m = String(out.serper_mode).trim().toLowerCase()
    if (m !== 'news' && m !== 'search') delete out.serper_mode
  }
  return out
}

/** Supabase PostgrestError is a plain object; throw Error for readable overlays. */
function throwIfError(error) {
  if (!error) return
  if (error instanceof Error) throw error
  const msg = typeof error.message === 'string' ? error.message : null
  if (msg) throw new Error(msg)
  throw new Error('Database request failed')
}

const DEFAULT_INSERT_CHUNK = 500

/**
 * Insert many rows in chunks. Postgres RETURNING order matches VALUES order.
 * @template T
 * @param {string} table
 * @param {T[]} rows
 * @param {{
 *   chunkSize?: number
 *   mapInsert?: (row: T) => Record<string, unknown>
 *   select?: string
 *   label?: string
 *   onChunk?: (info: { chunkIndex: number; insertedSoFar: number; totalRows: number }) => void
 * }} [opts]
 */
async function insertChunks(table, rows, opts = {}) {
  const {
    chunkSize = DEFAULT_INSERT_CHUNK,
    mapInsert = (r) => r,
    select = '*',
    label = table,
    onChunk,
  } = opts
  const out = []
  const totalRows = rows.length
  let chunkIndex = 0
  for (let offset = 0; offset < totalRows; offset += chunkSize) {
    chunkIndex += 1
    const chunk = rows.slice(offset, offset + chunkSize).map(mapInsert)
    if (chunk.length === 0) continue
    const { data, error } = await supabase.from(table).insert(chunk).select(select)
    try {
      throwIfError(error)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      throw new Error(`${label} chunk ${chunkIndex} (rows ~${offset + 1}-${offset + chunk.length}): ${msg}`)
    }
    const batch = data || []
    out.push(...batch)
    onChunk?.({ chunkIndex, insertedSoFar: out.length, totalRows })
  }
  return out
}

export const base44 = {
  entities: {
    Advertiser: {
      list: (sort, limit) => listTable('rt_advertisers', sort, limit),
      create: async (row) => {
        const { data, error } = await supabase.from('rt_advertisers').insert(row).select().single()
        throwIfError(error)
        return data
      },
      update: async (id, row) => {
        const { data, error } = await supabase
          .from('rt_advertisers')
          .update(stamp(row))
          .eq('id', id)
          .select()
          .single()
        throwIfError(error)
        return data
      },
      delete: async (id) => {
        const { error } = await supabase.from('rt_advertisers').delete().eq('id', id)
        throwIfError(error)
      },
      /** Chunked multi-row insert; returns rows in same order as input (per chunk order). */
      createMany: async (rows, options = {}) => {
        const { chunkSize, onChunk } = options
        return insertChunks('rt_advertisers', rows, {
          chunkSize,
          select: 'id, advertiser_name',
          label: 'rt_advertisers',
          onChunk,
        })
      },
    },
    Race: {
      list: (sort, limit) => listTable('rt_races', sort, limit),
      create: async (row) => {
        const { data, error } = await supabase.from('rt_races').insert(cleanRaceRow(row)).select().single()
        throwIfError(error)
        return data
      },
      update: async (id, row) => {
        const { data, error } = await supabase
          .from('rt_races')
          .update(stamp(cleanRaceRow(row)))
          .eq('id', id)
          .select()
          .single()
        throwIfError(error)
        return data
      },
      delete: async (id) => {
        const { error } = await supabase.from('rt_races').delete().eq('id', id)
        throwIfError(error)
      },
      createMany: async (rows, options = {}) => {
        const { chunkSize, onChunk } = options
        return insertChunks('rt_races', rows, {
          chunkSize,
          mapInsert: (r) => cleanRaceRow(r),
          select: 'id, advertiser_id, state, chamber, district',
          label: 'rt_races',
          onChunk,
        })
      },
    },
    Opponent: {
      list: (sort, limit) => listTable('rt_opponents', sort, limit),
      filter: async ({ race_id: raceId }) => {
        const { data, error } = await supabase.from('rt_opponents').select('*').eq('race_id', raceId)
        throwIfError(error)
        return data || []
      },
      create: async (row) => {
        const { data, error } = await supabase.from('rt_opponents').insert(row).select().single()
        throwIfError(error)
        return data
      },
      update: async (id, row) => {
        const { data, error } = await supabase.from('rt_opponents').update(row).eq('id', id).select().single()
        throwIfError(error)
        return data
      },
      delete: async (id) => {
        const { error } = await supabase.from('rt_opponents').delete().eq('id', id)
        throwIfError(error)
      },
      createMany: async (rows, options = {}) => {
        const { chunkSize, onChunk } = options
        return insertChunks('rt_opponents', rows, {
          chunkSize,
          select: 'id, race_id, opponent_name',
          label: 'rt_opponents',
          onChunk,
        })
      },
    },
    SweepResult: {
      list: (sort, limit) => listTable('rt_sweep_results', sort, limit),
    },
    SweepRun: {
      /**
       * @param {string} raceId
       * @param {{ limit?: number }} [opts]
       * @returns {Promise<{ rows: Record<string, unknown>[]; count: number }>}
       */
      listByRace: async (raceId, opts = {}) => {
        const limit = opts.limit ?? 100
        const { data, error, count } = await supabase
          .from('rt_sweep_runs')
          .select('*', { count: 'exact' })
          .eq('race_id', raceId)
          .order('created_at', { ascending: false })
          .limit(limit)
        throwIfError(error)
        return { rows: data || [], count: count ?? 0 }
      },
    },
    CsvImportLog: {
      /** @param {number} [limit] */
      listRecent: async (limit = 50) => {
        const { data, error } = await supabase
          .from('rt_csv_import_logs')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(limit)
        throwIfError(error)
        return data || []
      },
    },
  },
  functions: {
    invoke: async (name, payload) => {
      if (name === 'runSweep') {
        const res = await invokeEdgeFunction('run-sweep', {
          method: 'POST',
          body: JSON.stringify({ race_id: payload.race_id }),
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json.error || res.statusText)
        return json
      }
      if (name === 'serperPlayground') {
        return invokeSerperPlayground(payload)
      }
      throw new Error(`Unknown function ${name}`)
    },
  },
}

/** @param {Record<string, unknown>} body */
export async function invokeSerperPlayground(body) {
  const res = await invokeEdgeFunction('serper-playground', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || res.statusText)
  return json
}

/** Non-secret LLM routing + Serper defaults for Intel Sweep (authenticated GET; PATCH super_admin). */
export async function fetchRaceTrackerSweepConfig() {
  const res = await invokeEdgeFunction('race-tracker-sweep-config', { method: 'GET' })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || res.statusText)
  return json
}

/** PATCH serper_gl, serper_hl, serper_tbs — requires super_admin (Edge enforces). */
export async function patchRaceTrackerSweepConfigSerperLocale(body) {
  const res = await invokeEdgeFunction('race-tracker-sweep-config', {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || res.statusText)
  return json
}
